/**
 * TOLERANCE (GD&T feature control frame) model — Faz 8.3, OCS tolerance.rs
 * parity with a STRUCTURAL yap adaptation.
 *
 * OCS encodes the frame as ONE string with %%v dividers and inline
 * {\Fgdt;X} font switches; yap carries rows/cells so the dialog, renderer
 * and DXF writer share one typed model. The DXF serializer (Faz 1 eki)
 * flattens rows back into the %%v + {\Fgdt;X} encoding exactly like OCS
 * (row separator "\n"), so files round-trip into AutoCAD's TOLERANCE.
 *
 * Geometry: no leader association (OCS by design) — one insertion grip.
 */

/** gdt.lff symbol codes (OCS GdtSymbol set + n/p/m/l/s modifiers). */
export type GdtSymbol =
    | 'u' | 'c' | 'e' | 'g' | 'd' | 'k' | 'j' | 'r' | 'i' | 'f' | 'b' | 'a' | 'h' | 't'
    | 'n' | 'p';

export type GdtMaterial = 'm' | 'l' | 's';

/** One frame cell: plain text OR a gdt-font symbol glyph. */
export type ToleranceCell =
    | { text: string }
    | { symbol: GdtSymbol; font: 'gdt' };

/** A frame row (cells left → right). */
export type ToleranceRow = ToleranceCell[];

/** Entity data bag for type 'tolerance'. */
export interface ToleranceData {
    rows: ToleranceRow[];
    position: [number, number, number];
    /** Frame text height h (row cell height = 2h, OCS render rule). */
    textHeight: number;
    rotation?: number;
    color?: string;
}

/** Symbol names for the dialog grid (15 GD&T characteristic symbols). */
export const GDT_SYMBOLS: ReadonlyArray<{ code: GdtSymbol; label: string }> = [
    { code: 'u', label: 'Straightness' },
    { code: 'c', label: 'Flatness' },
    { code: 'e', label: 'Circularity' },
    { code: 'g', label: 'Cylindricity' },
    { code: 'd', label: 'Surface Profile' },
    { code: 'k', label: 'Line Profile' },
    { code: 'j', label: 'Position' },
    { code: 'r', label: 'Concentricity' },
    { code: 'i', label: 'Symmetry' },
    { code: 'f', label: 'Parallelism' },
    { code: 'b', label: 'Perpendicularity' },
    { code: 'a', label: 'Angularity' },
    { code: 'h', label: 'Circular Runout' },
    { code: 't', label: 'Total Runout' },
];

/** Material modifier labels (dialog). */
export const GDT_MATERIALS: ReadonlyArray<{ code: GdtMaterial; label: string }> = [
    { code: 'm', label: 'MMC' },
    { code: 'l', label: 'LMC' },
    { code: 's', label: 'RFS' },
];

/** Cell constructor shorthands. */
export const sym = (symbol: GdtSymbol): ToleranceCell => ({ symbol, font: 'gdt' });
export const txt = (text: string): ToleranceCell => ({ text });

/**
 * Build the classic fixed layout (OCS tolerance_dialog: 1 symbol + 2
 * tolerance compartments + 3 datum compartments):
 *   [symbol][diameter? value modifier?][datum1][datum2][datum3]
 * Empty optional compartments are dropped.
 */
export function buildToleranceRows(spec: {
    symbol: GdtSymbol | null;
    tolerance1: string;
    diameter1: boolean;
    material1: GdtMaterial | null;
    tolerance2: string;
    diameter2: boolean;
    material2: GdtMaterial | null;
    datum1: string;
    datum2: string;
    datum3: string;
    projectedZone: boolean;
    projectedHeight: string;
    datumIdentifier: string;
}): ToleranceRow[] {
    const tolCell = (value: string, diameter: boolean, material: GdtMaterial | null): ToleranceCell | null => {
        if (!value.trim() && !diameter && !material) return null;
        const parts: string[] = [];
        if (diameter) parts.push('⌀');
        parts.push(value.trim());
        if (material) parts.push(`Ⓜ${material.toUpperCase()}`);
        return txt(parts.join(''));
    };
    const row: ToleranceRow = [];
    if (spec.symbol) row.push(sym(spec.symbol));
    const t1 = tolCell(spec.tolerance1, spec.diameter1, spec.material1);
    if (t1) row.push(t1);
    const t2 = tolCell(spec.tolerance2, spec.diameter2, spec.material2);
    if (t2) row.push(t2);
    ([spec.datum1, spec.datum2, spec.datum3] as const).forEach((d) => {
        if (d.trim()) row.push(txt(d.trim()));
    });
    if (spec.projectedZone && spec.projectedHeight.trim()) row.push(txt(`⌷${spec.projectedHeight.trim()}`));
    const rows: ToleranceRow[] = [];
    if (row.length > 0) rows.push(row);
    if (spec.datumIdentifier.trim()) rows.push([txt(spec.datumIdentifier.trim())]);
    return rows;
}

/**
 * Flatten rows to the OCS text encoding: symbol cells wrap as
 * {\Fgdt;X}, cells join with %%v, rows join with "\n".
 */
export function encodeToleranceText(rows: ToleranceRow[]): string {
    return rows
        .map((row) => row.map((cell) => {
            if ('text' in cell) return cell.text;
            return `{\\Fgdt;${cell.symbol}}`;
        }).join('%%v'))
        .join('\n');
}

/** Reverse of encodeToleranceText (DXF import side). */
export function decodeToleranceText(encoded: string): ToleranceRow[] {
    const clean = encoded.replace(/"/g, '');
    return clean.split('\n').map((line) => {
        const cells: ToleranceRow = [];
        for (const part of line.split('%%v')) {
            const gdt = /^\{\\Fgdt;(.)\}$/.exec(part.trim());
            if (gdt) cells.push(sym(gdt[1] as GdtSymbol));
            else if (part.trim() !== '') cells.push(txt(part));
        }
        return cells;
    }).filter((row) => row.length > 0);
}
